import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { api, fmtBRL, fmtInt, fmtBRDate, fmtBRDateTime } from "@/lib/api";
import { apexBase, buildParams, useChartTheme, PALETTE } from "./pcShared";
import { UserCircle } from "@phosphor-icons/react";

function Stat({ label, value, testid }) {
  return (
    <div className="border border-zinc-200 dark:border-zinc-700 rounded px-2 py-1.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="font-mono text-sm font-bold">{value}</div>
    </div>
  );
}

export default function CollaboratorDetail({ code, applied }) {
  const dark = useChartTheme();
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(0);
  const limit = 25;

  useEffect(() => { setSkip(0); }, [code, applied]);
  useEffect(() => {
    api.get(`/production-reports/collaborator/${encodeURIComponent(code)}`, { params: { ...buildParams(applied), skip, limit } })
      .then(({ data }) => setData(data));
  }, [code, applied, skip]);

  if (!data) return <div className="tile p-6 text-center text-zinc-400 animate-pulse">Carregando perfil...</div>;

  const { profile, summary, by_stage, finance, daily_series, orders } = data;
  const base = apexBase(dark);

  return (
    <div className="tile p-4 space-y-4" data-testid="pc-collab-detail">
      <div className="flex flex-wrap items-center gap-3 border-b border-zinc-200 dark:border-zinc-700 pb-3">
        <UserCircle size={48} weight="fill" className="text-zinc-300" />
        <div>
          <div className="font-head font-black text-lg">{profile.name}</div>
          <div className="text-[11px] text-zinc-500">
            Código {profile.code || "—"} · {profile.sector || "Sem setor"} · {profile.role === "admin" ? "Administrador" : "Operador"}
            {profile.created_at && <> · Admissão {fmtBRDate(profile.created_at)} ({profile.days_in_company} dias de empresa)</>}
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <div className="text-center px-3">
            <div className="font-mono text-2xl font-black">#{summary.rank || "—"}</div>
            <div className="text-[9px] uppercase text-zinc-500">Ranking Produção</div>
          </div>
          <div className="text-center px-3">
            <div className="font-mono text-2xl font-black">#{finance.rank || "—"}</div>
            <div className="text-[9px] uppercase text-zinc-500">Ranking Financeiro</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Stat label="Dias trabalhados" value={summary.active_days ?? 0} testid="pc-cd-days" />
        <Stat label="Horas estimadas" value={`${summary.est_hours ?? 0}h`} testid="pc-cd-hours" />
        <Stat label="Movimentos" value={fmtInt(summary.movements ?? 0)} testid="pc-cd-movements" />
        <Stat label="Pedidos" value={fmtInt(summary.orders ?? 0)} testid="pc-cd-orders" />
        <Stat label="Peças" value={fmtInt(summary.pieces ?? 0)} testid="pc-cd-pieces" />
        <Stat label="SKUs" value={summary.skus ?? 0} testid="pc-cd-skus" />
        <Stat label="Peças/dia" value={fmtInt(summary.pieces_per_day ?? 0)} testid="pc-cd-ppd" />
        <Stat label="Retrabalhos" value={summary.reworks ?? 0} testid="pc-cd-reworks" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <div className="overline text-[10px] text-zinc-500 mb-1">EVOLUÇÃO DIÁRIA</div>
          {daily_series.length === 0 ? (
            <div className="text-xs text-zinc-400 py-6 text-center">Sem dados</div>
          ) : (
            <Chart type="area" height={200}
              series={[
                { name: "Peças", data: daily_series.map((d) => d.pieces) },
                { name: "Movimentos", data: daily_series.map((d) => d.movements) },
              ]}
              options={{
                ...base, chart: { ...base.chart, type: "area", zoom: { enabled: false } },
                stroke: { curve: "smooth", width: 2 }, fill: { opacity: 0.15 },
                xaxis: { categories: daily_series.map((d) => d.d), labels: { rotate: -45, style: { fontSize: "9px" } } },
              }} />
          )}
        </div>
        <div>
          <div className="overline text-[10px] text-zinc-500 mb-1">ETAPAS CONCLUÍDAS</div>
          {by_stage.length === 0 ? (
            <div className="text-xs text-zinc-400 py-6 text-center">Sem dados</div>
          ) : (
            <Chart type="bar" height={200}
              series={[{ name: "Movimentos", data: by_stage.map((s) => s.movements) }]}
              options={{
                ...base, plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
                xaxis: { categories: by_stage.map((s) => s.label) }, colors: [PALETTE[4]],
              }} />
          )}
        </div>
      </div>

      <div>
        <div className="overline text-[10px] text-zinc-500 mb-1">FATURAMENTO DO COLABORADOR</div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <Stat label="Pedidos finalizados" value={fmtInt(finance.finalized_orders)} testid="pc-cd-fin-orders" />
          <Stat label="Valor total" value={fmtBRL(finance.finalized_value)} testid="pc-cd-fin-value" />
          <Stat label="Valor médio" value={fmtBRL(finance.avg_order_value)} testid="pc-cd-fin-avg" />
          <Stat label="Maior pedido" value={fmtBRL(finance.max_order)} testid="pc-cd-fin-max" />
          <Stat label="Menor pedido" value={fmtBRL(finance.min_order)} testid="pc-cd-fin-min" />
          <Stat label="% da fábrica" value={`${finance.share_pct || 0}%`} testid="pc-cd-fin-share" />
        </div>
      </div>

      <div>
        <div className="overline text-[10px] text-zinc-500 mb-1">PEDIDOS EXECUTADOS ({fmtInt(orders.total)})</div>
        <div className="overflow-x-auto">
          <table className="table w-full text-xs">
            <thead>
              <tr><th>Data/Hora</th><th>Pedido</th><th>Cliente</th><th>Etapa realizada</th><th className="text-right">Peças</th><th className="text-right">Valor</th><th>SKUs</th></tr>
            </thead>
            <tbody>
              {orders.items.map((r) => (
                <tr key={`${r.order_id}-${r.at}`}>
                  <td className="font-mono">{fmtBRDateTime(r.at)}</td>
                  <td className="font-mono font-bold">#{r.bling_number}</td>
                  <td className="truncate max-w-[160px]">{r.customer}</td>
                  <td>{r.from_label} → {r.to_label}</td>
                  <td className="text-right font-mono">{fmtInt(r.cups)}</td>
                  <td className="text-right font-mono">{fmtBRL(r.value)}</td>
                  <td className="truncate max-w-[140px] text-zinc-500">{r.skus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end gap-1 mt-1">
          <button className="btn !py-0.5 !px-2" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="pc-cd-prev">‹</button>
          <span className="text-[10px] text-zinc-500">{Math.floor(skip / limit) + 1} / {Math.max(1, Math.ceil(orders.total / limit))}</span>
          <button className="btn !py-0.5 !px-2" disabled={skip + limit >= orders.total} onClick={() => setSkip(skip + limit)} data-testid="pc-cd-next">›</button>
        </div>
      </div>
    </div>
  );
}
